import React from 'react';
import { useTranslation } from 'react-i18next';
import { TrendingUp } from 'lucide-react';
import {
  useTopMovers,
  useAbcAnalysis,
  useReorderSignals,
} from '../hooks/useReportingV2';
import { TopMoversCard } from './performance/TopMoversCard';
import { AbcParetoCard } from './performance/AbcParetoCard';
import { ReorderSignalsCard } from './performance/ReorderSignalsCard';

interface PerformanceTabProps {
  organizationId: string | null;
  startDate: Date;
  endDate: Date;
  locationId: string | null;
  /** Only true when this tab is active — gates every query. */
  active: boolean;
}

/**
 * Performance tab (period-based). Movers snapshot, then the ABC/Pareto cut and
 * the reorder-signals payoff table. Every query is gated on `active`, and all
 * hooks fire in parallel (no chaining) so the tab's round-trips overlap.
 *
 * Blank consumption + the full SKU-velocity report now live on the Consumption
 * tab; print throughput and the inventory-turns ("sell-through") chart were
 * removed (POD blanks aren't pick-and-pack, so those framings misled).
 */
export const PerformanceTab: React.FC<PerformanceTabProps> = ({
  organizationId,
  startDate,
  endDate,
  locationId,
  active,
}) => {
  const { t } = useTranslation('inventory');
  const period = { organizationId, startDate, endDate, locationId, enabled: active };

  const topMovers = useTopMovers({ ...period, limit: 15 });
  const abc = useAbcAnalysis(period);
  const reorder = useReorderSignals({ organizationId, locationId, enabled: active });

  return (
    <div className="space-y-8">
      {/* Movers snapshot */}
      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-threadheads-purple" aria-hidden="true" />
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {t('reporting.v2.moversLane', 'Top movers')}
          </h2>
        </div>
        <TopMoversCard loading={topMovers.isLoading} rows={topMovers.data ?? []} />
      </section>

      {/* ABC / Pareto */}
      <AbcParetoCard loading={abc.isLoading} rows={abc.data ?? []} />

      {/* Reorder payoff */}
      <ReorderSignalsCard loading={reorder.isLoading} rows={reorder.data ?? []} />
    </div>
  );
};
